<template>
  <div class="map-picker">
    <div class="map-bar" v-if="searchable">
      <el-autocomplete
        v-model="keyword"
        :fetch-suggestions="querySearch"
        placeholder="搜索地点/地址，如：谷里街道周村社区119号"
        style="width:280px"
        clearable
        @select="onSelectSuggestion"
        @keyup.enter="doSearch" />
      <el-button type="primary" @click="doSearch">搜索</el-button>
      <span class="coord-now">当前：{{ displayLng }}, {{ displayLat }}</span>
    </div>

    <div ref="mapEl" class="map-box" :style="{ height }">
      <div v-if="loadError" class="map-error">
        地图加载失败：请确认已配置有效的高德 Web 端 key，且当前网络可访问 webapi.amap.com。
      </div>
    </div>

    <div class="coord-row">
      <span class="lbl">经度</span>
      <el-input v-model="lngInput" size="small" style="width:150px" @change="onManual" />
      <span class="lbl">纬度</span>
      <el-input v-model="latInput" size="small" style="width:150px" @change="onManual" />
      <el-button size="small" text type="primary" @click="useNanjing">定位到南京</el-button>
      <el-button size="small" text @click="clearPos" :disabled="!hasPos">清除标注</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'

// 高德(AMap) JS API 2.0 key，优先从环境变量注入（避免密钥入库）。
// 在项目根目录 .env 中配置 VUE_APP_AMAP_KEY 与 VUE_APP_AMAP_SECURITY_CODE（参考 .env.example）。
const AMAP_KEY = process.env.VUE_APP_AMAP_KEY || 'ed62f646a11d1babb55dd6a94190ed00'
// 高德 JS API 2.0 安全密钥：2021-12 之后申请的 key 必须配置，否则地图/搜索均加载失败。
const AMAP_SECURITY_CODE = process.env.VUE_APP_AMAP_SECURITY_CODE || '625c3101178e183f7794bb6fe6aefd83'

const props = defineProps({
  lng: { type: Number, default: null },
  lat: { type: Number, default: null },
  height: { type: String, default: '320px' },
  searchable: { type: Boolean, default: true },
  city: { type: String, default: '南京' },
  zoom: { type: Number, default: 12 }
})
const emit = defineEmits(['update:lng', 'update:lat', 'change'])

const mapEl = ref(null)
const keyword = ref('')
const lngInput = ref('')
const latInput = ref('')
const loadError = ref(false)
let map = null
let marker = null

const hasPos = computed(() => props.lng != null && props.lat != null)
const displayLng = computed(() => (props.lng != null ? (+props.lng).toFixed(6) : '—'))
const displayLat = computed(() => (props.lat != null ? (+props.lat).toFixed(6) : '—'))

let amapPromise = null
function loadAMap() {
  if (window.AMap) return Promise.resolve(window.AMap)
  if (amapPromise) return amapPromise
  amapPromise = new Promise((resolve, reject) => {
    if (AMAP_SECURITY_CODE) {
      window._AMapSecurityConfig = { securityJsCode: AMAP_SECURITY_CODE }
    }
    const script = document.createElement('script')
    script.src = 'https://webapi.amap.com/maps?v=2.0&key=' + encodeURIComponent(AMAP_KEY)
    script.async = true
    script.onload = () => resolve(window.AMap)
    script.onerror = () => reject(new Error('AMap load failed'))
    document.head.appendChild(script)
  })
  return amapPromise
}

function loadPlugins(names) {
  return new Promise((resolve, reject) => {
    if (!window.AMap) return reject(new Error('AMap not loaded'))
    window.AMap.plugin(names, () => resolve(window.AMap))
  })
}

function apply(lng, lat) {
  lng = +(+lng).toFixed(6)
  lat = +(+lat).toFixed(6)
  lngInput.value = lng
  latInput.value = lat
  emit('update:lng', lng)
  emit('update:lat', lat)
  emit('change', { lng, lat })
}

function setMarker(lng, lat) {
  if (!map) return
  if (!marker) {
    marker = new window.AMap.Marker({ map, position: [lng, lat], draggable: true, title: '粮库位置' })
    marker.on('dragend', (e) => { const p = e.lnglat; apply(p.getLng(), p.getLat()) })
  } else {
    marker.setPosition([lng, lat])
  }
  map.setCenter([lng, lat])
  map.setZoom(16)
  apply(lng, lat)
}

function clearPos() {
  if (marker) { marker.setMap(null); marker = null }
  lngInput.value = ''
  latInput.value = ''
  emit('update:lng', null)
  emit('update:lat', null)
  emit('change', { lng: null, lat: null })
}

function onManual() {
  const lng = Number(lngInput.value)
  const lat = Number(latInput.value)
  if (lngInput.value === '' || latInput.value === '' || isNaN(lng) || isNaN(lat)) return
  if (lng < -180 || lng > 180 || lat < -90 || lat > 90) { ElMessage.warning('经纬度范围不合法'); return }
  setMarker(lng, lat)
}

function useNanjing() {
  const c = [118.797, 32.06]
  if (map) { map.setCenter(c); map.setZoom(12) }
  setMarker(c[0], c[1])
}

/* 搜索：POI 关键字 → 地址解析 → 区域/城市中心，逐级兜底，确保地图一定会移动 */
async function doSearch() {
  const text = keyword.value.trim()
  if (!text || !map) return
  if (!window.AMap) { ElMessage.warning('地图未加载，请检查高德 key 是否配置'); return }
  try {
    await loadPlugins(['AMap.PlaceSearch', 'AMap.Geocoder', 'AMap.DistrictSearch'])
    const ps = new window.AMap.PlaceSearch({ city: props.city || '全国', pageSize: 5 })
    ps.search(text, (status, result) => {
      if (status === 'complete' && result && result.poiList && result.poiList.pois.length) {
        const p = result.poiList.pois[0].location
        if (p) { setMarker(p.lng, p.lat); return }
      }
      // POI 没结果 → 地址解析（可能只到街道/区县级）
      geocodeThenFallback(text)
    })
  } catch (e) {
    ElMessage.warning('搜索失败：请确认高德 key 有效且已开通 JS API')
    console.error(e)
  }
}

function geocodeThenFallback(address) {
  try {
    const geocoder = new window.AMap.Geocoder({ city: props.city || '全国' })
    geocoder.getLocation(address, (status, result) => {
      if (status === 'complete' && result && result.geocodes && result.geocodes.length) {
        const loc = result.geocodes[0].location
        if (loc) {
          // 解析到街道/区县级：移动地图到该区域，不打精确标记，让用户自己点
          map.setCenter([loc.lng, loc.lat])
          map.setZoom(15)
          ElMessage.info('已定位到「' + address + '」大致区域，请在地图上点击精确标注粮库位置')
          return
        }
      }
      // 仍无结果 → 退到城市/区域中心，提示手动标注
      fallbackCity(address)
    })
  } catch (e) {
    fallbackCity(address)
  }
}

function fallbackCity(address) {
  try {
    const ds = new window.AMap.DistrictSearch({ subdistrict: 0, extensions: 'all' })
    ds.search(props.city || '南京', (status, result) => {
      if (status === 'complete' && result && result.districtList && result.districtList.length) {
        const c = result.districtList[0].center
        if (c) { map.setCenter([c.lng, c.lat]); map.setZoom(11) }
      }
      ElMessage.warning('未找到「' + address + '」精确位置，已定位到' + (props.city || '南京') + '，请手动点击标注')
    })
  } catch (e) {
    ElMessage.warning('未找到「' + address + '」，请手动在地图上点击标注')
  }
}

/* 输入联想下拉 */
async function querySearch(q, cb) {
  const text = (q || '').trim()
  if (!text || !window.AMap) return cb([])
  try {
    await loadPlugins(['AMap.AutoComplete'])
    const ac = new window.AMap.AutoComplete({ city: props.city || '全国' })
    ac.search(text, (status, result) => {
      if (status === 'complete' && result && result.tips && result.tips.length) {
        const list = result.tips
          .filter(t => t.location && t.location.lng)
          .map(t => ({
            value: (t.name || '') + (t.district ? '（' + t.district + '）' : ''),
            lng: t.location.lng,
            lat: t.location.lat
          }))
        cb(list)
      } else cb([])
    })
  } catch (e) { cb([]) }
}

function onSelectSuggestion(item) {
  if (item && item.lng != null && item.lat != null) {
    setMarker(item.lng, item.lat)
  }
}

onMounted(async () => {
  try {
    const AMap = await loadAMap()
    const center = (props.lng != null && props.lat != null) ? [props.lng, props.lat] : [118.797, 32.06]
    map = new AMap.Map(mapEl.value, { zoom: props.zoom, center })
    map.on('click', (e) => { setMarker(e.lnglat.getLng(), e.lnglat.getLat()) })
    if (props.lng != null && props.lat != null) setMarker(props.lng, props.lat)
    setTimeout(() => map && map.resize(), 350)
  } catch (e) {
    loadError.value = true
    console.error(e)
  }
})

watch(() => [props.lng, props.lat], ([nl, nt]) => {
  if (nl != null && nt != null && map) {
    if (!marker) setMarker(nl, nt)
    else { const p = marker.getPosition(); if (p.getLng() !== nl || p.getLat() !== nt) setMarker(nl, nt) }
  } else if ((nl == null || nt == null) && marker) {
    clearPos()
  }
})

onBeforeUnmount(() => {
  if (map) { map.destroy(); map = null; marker = null }
})
</script>

<style scoped>
.map-picker { border: 1px solid #dcdfe6; border-radius: 6px; overflow: hidden; }
.map-bar { display: flex; align-items: center; gap: 8px; padding: 8px; background: #f5f7fa; border-bottom: 1px solid #ebeef5; flex-wrap: wrap; }
.coord-now { font-size: 12px; color: #909399; margin-left: auto; }
.map-box { width: 100%; position: relative; background: #eef0f2; }
.map-error { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 16px; text-align: center; color: #f56c6c; font-size: 13px; background: #fdf0f0; }
.coord-row { display: flex; align-items: center; gap: 8px; padding: 8px; background: #fafafa; border-top: 1px solid #ebeef5; flex-wrap: wrap; }
.coord-row .lbl { font-size: 13px; color: #606266; }
</style>
